import { useState, useCallback } from 'react'
import {
  type Difficulty, type Schedule, type PlacedPill, type Medicine, type Bottle,
  generateGame, emptyCompartments, checkResult, SCHEDULES,
} from './pillboxData'

export type { Difficulty, Schedule, PlacedPill, Medicine, Bottle }
export { SCHEDULES }

export interface PillboxState {
  medicines:        Medicine[]
  bottles:          Bottle[]
  selectedBottleId: string | null
  compartments:     Record<Schedule, PlacedPill[]>
  checked:          boolean
  wrongSchedules:   Schedule[]
  moves:            number
  startTime:        number | null
  endTime:          number | null
  gameComplete:     boolean
}

const MAX_PILLS_PER_COMPARTMENT = 20

function initState(difficulty: Difficulty): PillboxState {
  const { medicines, bottles } = generateGame(difficulty)
  return {
    medicines,
    bottles,
    selectedBottleId: null,
    compartments:     emptyCompartments(),
    checked:          false,
    wrongSchedules:   [],
    moves:            0,
    startTime:        null,
    endTime:          null,
    gameComplete:     false,
  }
}

export function usePillbox(difficulty: Difficulty) {
  const [state, setState] = useState<PillboxState>(() => initState(difficulty))

  const resetGame = useCallback(() => setState(initState(difficulty)), [difficulty])

  const selectBottle = useCallback((bottleId: string) => {
    setState((prev) => {
      if (prev.gameComplete) return prev
      const startTime = prev.startTime ?? Date.now()
      if (prev.selectedBottleId === bottleId) return { ...prev, selectedBottleId: null }
      return { ...prev, selectedBottleId: bottleId, startTime }
    })
  }, [])

  const deselectBottle = useCallback(() => {
    setState((prev) => ({ ...prev, selectedBottleId: null }))
  }, [])

  const addPillToCompartment = useCallback((schedule: Schedule) => {
    setState((prev) => {
      if (!prev.selectedBottleId || prev.gameComplete) return prev
      if (prev.compartments[schedule].length >= MAX_PILLS_PER_COMPARTMENT) return prev

      const bottle = prev.bottles.find(b => b.id === prev.selectedBottleId)
      if (!bottle) return prev

      const newPill: PlacedPill = {
        id:         `pill-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        medicineId: bottle.medicineId,
        pillShape:  bottle.pillShape,
        pillColor:  bottle.pillColor,
      }
      return {
        ...prev,
        compartments:   { ...prev.compartments, [schedule]: [...prev.compartments[schedule], newPill] },
        moves:          prev.moves + 1,
        startTime:      prev.startTime ?? Date.now(),
        checked:        false,
        wrongSchedules: [],
      }
    })
  }, [])

  const removePillFromCompartment = useCallback((schedule: Schedule, pillId: string) => {
    setState((prev) => {
      if (prev.gameComplete) return prev
      return {
        ...prev,
        compartments:   { ...prev.compartments, [schedule]: prev.compartments[schedule].filter(p => p.id !== pillId) },
        moves:          prev.moves + 1,
        checked:        false,
        wrongSchedules: [],
      }
    })
  }, [])

  const checkAnswer = useCallback(() => {
    setState((prev) => {
      if (prev.gameComplete) return prev
      const wrong        = checkResult(prev.medicines, prev.compartments)
      const gameComplete = wrong.length === 0
      return {
        ...prev,
        checked:        true,
        wrongSchedules: wrong,
        moves:          prev.moves + 1,
        gameComplete,
        endTime:        gameComplete ? Date.now() : prev.endTime,
      }
    })
  }, [])

  return {
    state,
    resetGame,
    selectBottle,
    deselectBottle,
    addPillToCompartment,
    removePillFromCompartment,
    checkAnswer,
    SCHEDULES,
  }
}
